/**
 * VacationRequestPage
 *
 * Form page for submitting a practitioner vacation / time-off request.
 *
 * API endpoints:
 *   GET  /api/clinics/                      → populates the Practice selector for org-level users
 *        (hits the `clinics_Clinic` table)
 *   GET  /api/practitioners/?clinic=<code>  → populates the Employee selector for the selected clinic
 *        (hits `practitioner_profile_PractitionerProfile`)
 *   POST /api/practitioner-vacations/       → creates a new vacation record with status="pending"
 *        (writes to `practitioner_vacations` table — model lives in practitioners app)
 *
 * Scheduling / vacation data written:
 *   - practitioner    — FK to the Practitioner (clinic-assignment); clinic is implicit via this FK
 *   - date_ranges     — the created row's single [{ start_date, end_date, ... }] entry
 *   - leave_type      — "vacation" | "sick" | "personal" | "unpaid"
 *   - reason          — optional free-text note from the employee
 *   - status          — always "pending" on creation; approvals flow through ApprovalsPage
 *
 * Key fields: practitioner, date_ranges, leave_type, reason, status
 */
import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  usePractitioners,
  useCreatePractitionerVacation,
  useClinics,
  useClinicDetail,
} from "@api/hooks";
import { getHolidayMap } from "@utils/canadianHolidays";
import { useAuth } from "@api/useAuth";
import { getRoster } from "@utils/practitionerUtils";
import { safeText } from "@utils/utils";
import { canAccessAllClinics } from "@utils";
import { Button, Card, showSuccess } from "@common";

// ── Helpers ────────────────────────────────────────────────────────────────────

const LEAVE_TYPES = [
  { key: "vacation", label: "Vacation", sub: "Planned paid time off" },
  { key: "sick", label: "Sick", sub: "Illness or medical" },
  { key: "personal", label: "Personal", sub: "Personal day" },
  { key: "unpaid", label: "Unpaid leave", sub: "Unpaid time off" },
];

function daysBetween(start, end) {
  if (!start || !end || end < start) return 0;
  // Parse as local midnight to avoid UTC offset shifting the date by one day.
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  // Divide by ms-per-day and add 1 so a single-day request (start === end) counts as 1, not 0.
  return Math.max(0, Math.round((e - s) / 86400000) + 1);
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function VacationRequestPage() {
  const { clinicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    clinicCode: clinicId,
    practitionerId: null,
    leaveType: "vacation",
    startDate: "",
    endDate: "",
    reason: "",
  });
  const [error, setError] = useState("");

  // Clinic list for the Practice selector
  const canListAll = canAccessAllClinics(user);
  const { data: clinicsData } = useClinics({ limit: 10000, offset: 0 }, { enabled: canListAll });
  const allClinics = useMemo(() => {
    if (canListAll) {
      return (clinicsData?.results || [])
        .slice()
        .sort((a, b) => (a.name || a.clinic || "").localeCompare(b.name || b.clinic || ""));
    }
    const assigned = Array.isArray(user?.clinic) ? user.clinic : user?.clinic ? [user.clinic] : [];
    return assigned.length
      ? assigned.map((c) => ({ clinic: c, name: "" }))
      : [{ clinic: clinicId, name: "" }];
  }, [clinicsData, canListAll, user, clinicId]);

  // Practitioner list for the Employee selector (scoped to selected clinic)
  const { data: practitionersData } = usePractitioners({ clinic: form.clinicCode });
  const roster = useMemo(() => {
    return getRoster(practitionersData)
      .filter((p) => safeText(p?.name) && safeText(p?.status).toLowerCase() !== "inactive")
      .slice()
      .sort((a, b) => safeText(a?.name).localeCompare(safeText(b?.name)));
  }, [practitionersData]);

  const createMut = useCreatePractitionerVacation();

  // Fetch the selected clinic's province so we can show province-correct holidays.
  const { data: clinicDetail } = useClinicDetail(form.clinicCode);
  const holidayMap = useMemo(() => {
    const province = clinicDetail?.province || null;
    const year = new Date().getFullYear();
    // Include next year so holidays are correct when the request spans Dec → Jan.
    return getHolidayMap([year, year + 1], province);
  }, [clinicDetail]);

  // Collect any statutory holidays that fall within the selected date range.
  const holidaysInRange = useMemo(() => {
    if (!form.startDate || !form.endDate || form.endDate < form.startDate) return [];
    const result = [];
    const end = new Date(form.endDate + "T00:00:00");
    const cur = new Date(form.startDate + "T00:00:00");
    while (cur <= end) {
      const s =
        cur.getFullYear() +
        "-" +
        String(cur.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(cur.getDate()).padStart(2, "0");
      if (holidayMap[s]) result.push({ dateStr: s, ...holidayMap[s] });
      cur.setDate(cur.getDate() + 1);
    }
    return result;
  }, [form.startDate, form.endDate, holidayMap]);

  const days = daysBetween(form.startDate, form.endDate);
  // Effective vacation days = calendar days minus paid statutory holidays in the range.
  // The 14-day cap is applied to this number, not raw calendar days.
  const paidHolidaysInRange = holidaysInRange.filter((h) => h.isPaidStat);
  const effectiveVacationDays = Math.max(0, days - paidHolidaysInRange.length);

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit() {
    setError("");
    if (!form.practitionerId) {
      setError("Please select an employee.");
      return;
    }
    if (!form.startDate || !form.endDate) {
      setError("Please select start and end dates.");
      return;
    }
    if (form.endDate < form.startDate) {
      setError("End date must be on or after the start date.");
      return;
    }
    // Only the "vacation" type has a cap. Cap is applied to vacation days (calendar days
    // minus paid statutory holidays), not raw calendar days.
    if (form.leaveType === "vacation" && effectiveVacationDays > 14) {
      const note =
        paidHolidaysInRange.length > 0
          ? ` (${days} calendar day${days !== 1 ? "s" : ""} minus ${paidHolidaysInRange.length} statutory holiday${paidHolidaysInRange.length !== 1 ? "s" : ""} = ${effectiveVacationDays} vacation day${effectiveVacationDays !== 1 ? "s" : ""})`
          : "";
      setError(`Vacation requests cannot exceed 14 vacation days.${note}`);
      return;
    }
    try {
      // Flat payload — the backend creates a NEW practitioner_vacations row for this
      // request (status "pending"), rather than appending to an existing row. No
      // date_ranges array or status field is sent from the frontend.
      await createMut.mutateAsync({
        practitioner: form.practitionerId,
        start_date: form.startDate,
        end_date: form.endDate,
        leave_type: form.leaveType,
        reason: form.reason.trim() || null,
      });
      showSuccess("Vacation request submitted successfully.");
      navigate(`/report/${form.clinicCode}/schedule/approvals`);
    } catch {
      setError("Failed to submit request. Please try again.");
    }
  }

  return (
    <div className="min-h-screen bg-palette-gray-soft">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-palette-gray-divider px-6 py-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4">
            <Button
              variant="link"
              onClick={() => navigate(`/report/${clinicId}/schedule`)}
              className="text-palette-gray-placeholder hover:text-palette-slate-badge text-sm"
            >
              ← Back
            </Button>
            <div>
              <h1 className="text-lg font-bold text-palette-slate-text">Request vacation</h1>
              <p className="text-xs text-palette-gray-secondary">Submit time-off for approval</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/report/${clinicId}/schedule/approvals`)}
            className="px-4 py-2 rounded-xl border border-palette-gray-divider text-palette-slate-heading"
          >
            View approvals
          </Button>
        </div>
      </div>

      <div className="px-6 py-8 w-full">
        <Card className="shadow-sm border border-palette-gray-subtle" bodyClassName="p-8">
          {error && (
            <div className="mb-6 px-4 py-3 rounded-xl bg-palette-red-soft border border-palette-red-border text-sm text-palette-red-badge">
              {error}
            </div>
          )}

          {/* Practice + Employee */}
          <div className="grid grid-cols-2 gap-6 mb-7">
            <div>
              <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
                Practice
              </label>
              <select
                value={form.clinicCode}
                onChange={(e) => {
                  update("clinicCode", e.target.value);
                  update("practitionerId", null);
                }}
                className="w-full rounded-xl border border-palette-gray-divider px-4 py-3 text-sm text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring focus:border-palette-blue-indicator"
              >
                {allClinics.map((c) => (
                  <option key={c.clinic} value={c.clinic}>
                    {c.name?.trim() ? c.name : c.clinic}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
                Employee
              </label>
              <select
                value={form.practitionerId ?? ""}
                onChange={(e) =>
                  update("practitionerId", e.target.value ? Number(e.target.value) : null)
                }
                className="w-full rounded-xl border border-palette-gray-divider px-4 py-3 text-sm text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring focus:border-palette-blue-indicator"
              >
                <option value="">Select employee…</option>
                {roster.map((p) => (
                  <option key={p.id} value={p.id}>
                    {safeText(p.name)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Per-clinic leave notice — shown when an employee is selected so the
               user understands that leave is scoped to the selected clinic only.
               Multi-clinic practitioners must submit a separate request per clinic. */}
          {form.practitionerId && (
            <div className="mb-5 flex items-start gap-2.5 px-4 py-3 rounded-xl bg-palette-amber-soft border border-palette-amber-border">
              <span className="text-palette-amber-label text-base shrink-0 mt-0.5">⚠️</span>
              <p className="text-xs text-palette-amber-heading leading-relaxed">
                <span className="font-semibold">Leave is applied to this clinic only.</span> If this
                practitioner works at multiple clinics, submit a separate leave request for each one
                — even if the dates are the same.
              </p>
            </div>
          )}

          {/* Type of Leave */}
          <div className="mb-7">
            <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-3 block">
              Type of Leave
            </label>
            <div className="grid grid-cols-4 gap-3">
              {LEAVE_TYPES.map(({ key, label, sub }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => update("leaveType", key)}
                  className={`rounded-xl border p-4 text-left transition-all ${
                    form.leaveType === key
                      ? "border-palette-blue-label bg-palette-blue-soft ring-2 ring-palette-blue-ring"
                      : "border-palette-gray-divider bg-white hover:border-palette-gray-border hover:bg-palette-gray-soft"
                  }`}
                >
                  <p
                    className={`text-sm font-semibold ${
                      form.leaveType === key
                        ? "text-palette-blue-heading"
                        : "text-palette-slate-text"
                    }`}
                  >
                    {label}
                  </p>
                  <p className="text-xs text-palette-gray-placeholder mt-0.5 leading-snug">{sub}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Start + End date */}
          <div className="grid grid-cols-2 gap-6 mb-3">
            <div>
              <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
                Start Date
              </label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => update("startDate", e.target.value)}
                className="w-full rounded-xl border border-palette-gray-divider px-4 py-3 text-sm text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring focus:border-palette-blue-indicator"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
                End Date
              </label>
              <input
                type="date"
                value={form.endDate}
                min={form.startDate || undefined}
                onChange={(e) => update("endDate", e.target.value)}
                className="w-full rounded-xl border border-palette-gray-divider px-4 py-3 text-sm text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring focus:border-palette-blue-indicator"
              />
            </div>
          </div>

          {/* Days calculated — shows vacation days (holidays excluded) when applicable */}
          <div
            className={`flex items-center gap-2 mb-4 px-4 py-3 rounded-xl border transition-colors ${
              days > 0
                ? "bg-palette-gray-soft border-palette-gray-divider"
                : "bg-transparent border-transparent"
            }`}
          >
            {days > 0 ? (
              <>
                <span className="text-palette-gray-placeholder text-sm">📅</span>
                {paidHolidaysInRange.length > 0 ? (
                  /* Show breakdown when holidays reduce the vacation day count */
                  <span className="text-sm font-medium text-palette-slate-badge">
                    <span className="font-semibold text-palette-slate-heading">
                      {effectiveVacationDays}
                    </span>{" "}
                    vacation day{effectiveVacationDays !== 1 ? "s" : ""}
                    <span className="text-palette-gray-placeholder font-normal">
                      {" "}
                      ({days} calendar − {paidHolidaysInRange.length} holiday
                      {paidHolidaysInRange.length !== 1 ? "s" : ""})
                    </span>
                  </span>
                ) : (
                  <span className="text-sm font-medium text-palette-slate-badge">
                    {days} day{days !== 1 ? "s" : ""} requested
                  </span>
                )}
              </>
            ) : (
              <span className="text-sm text-palette-gray-border">Select dates to see duration</span>
            )}
          </div>

          {/* Statutory holidays in the selected range — listed so the employee sees which days are excluded */}
          {holidaysInRange.length > 0 && (
            <div className="mb-7 px-4 py-3 rounded-xl bg-palette-red-soft border border-palette-red-border">
              <p className="text-xs font-semibold text-palette-red-heading mb-2">
                🏛 Statutory holiday{holidaysInRange.length > 1 ? "s" : ""} in this period
              </p>
              <ul className="space-y-1">
                {holidaysInRange.map((h) => (
                  <li
                    key={h.dateStr}
                    className="flex items-center gap-2 text-xs text-palette-red-heading"
                  >
                    <span className="font-medium">
                      {new Date(h.dateStr + "T00:00:00").toLocaleDateString("en-CA", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                    <span className="text-palette-red-indicator">·</span>
                    <span>{h.name}</span>
                    {h.isPaidStat && (
                      <span className="ml-auto font-semibold text-palette-red-label">
                        Paid Stat
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Reason */}
          <div className="mb-8">
            <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
              Reason (Optional)
            </label>
            <textarea
              value={form.reason}
              onChange={(e) => update("reason", e.target.value)}
              placeholder="Add any context for the manager…"
              rows={4}
              className="w-full resize-none rounded-xl border border-palette-gray-divider px-4 py-3 text-sm text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring focus:border-palette-blue-indicator"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4">
            <Button
              variant="link"
              onClick={() => navigate(`/report/${clinicId}/schedule`)}
              className="text-palette-gray-secondary hover:text-palette-slate-heading text-sm font-medium"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={createMut.isPending}
              className="px-6 py-2.5 rounded-xl font-semibold"
            >
              {createMut.isPending ? "Submitting…" : "Submit request"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
